import { marked } from "marked";
import { AppConfig } from "./AppConfig.js";
import { ApiService } from "./ApiService.js";
import { streamAsk, metaRespuesta } from "../services/KbApiService.js";
import { escapeHtml } from "./sanitize.js";
import { rutaArticuloHtml } from "./subcategorias.js";

/**
 * Muestra debajo de la respuesta los artículos y archivos consultados.
 * Va como hermano de la burbuja (no dentro) para que collectChatHistory no lo
 * mande al LLM como parte de la respuesta.
 */
export function renderFuentes(bubble, fuentesDetalle) {
  if (!bubble || !Array.isArray(fuentesDetalle) || !fuentesDetalle.length) return;
  const el = document.createElement("div");
  el.className = "kb-chat-sources small text-muted mb-2";
  el.innerHTML = `<i class="fas fa-book-open me-1"></i>Fuentes: ${fuentesDetalle.map(enlaceArticulo).join('<span class="mx-1">·</span>')}`;
  bubble.after(el);
}

function enlaceArticulo(f) {
  // Categoría / subcategoría antes del título: deja ver de dónde salió la respuesta
  // (las conversaciones guardadas antes de este cambio no las traen)
  const sub = AppConfig.features.subcategories ? f.subcategoria : "";
  const ruta = f.categoria
    ? `<span class="kb-fuente-ruta">${rutaArticuloHtml(f.categoria, sub)}</span><i class="fas fa-angle-right mx-1 kb-fuente-sep"></i>`
    : "";
  const titulo = escapeHtml(f.titulo || "Artículo");
  const archivo = f.nombre ? ` <i class="fas fa-arrow-right mx-1"></i>${escapeHtml(f.nombre)}` : "";
  const completo = [f.categoria, sub, f.titulo || "Artículo", f.nombre].filter(Boolean).join(" / ");
  // data-*: la vista chat abre la fuente en el panel lateral en lugar de navegar
  const adjunto = f.adjuntoId ? ` data-adjunto-id="${escapeHtml(f.adjuntoId)}"` : "";
  return `<a href="#article?id=${encodeURIComponent(f.articuloId)}" class="kb-chat-source" title="${escapeHtml(completo)}" data-articulo-id="${escapeHtml(f.articuloId)}"${adjunto}>${ruta}${titulo}${archivo}</a>`;
}

/**
 * Fuentes, pregunta corregida y "¿Quisiste decir?" de una respuesta. Todo va fuera de la
 * burbuja para que collectChatHistory no lo mande al LLM.
 */
export function renderMetaRespuesta(bubble, { fuentesDetalle, correccion, sugerencias } = {}) {
  if (!bubble) return;
  renderFuentes(bubble, fuentesDetalle);
  if (Array.isArray(sugerencias) && sugerencias.length) {
    const el = document.createElement("div");
    el.className = "kb-chat-sources kb-chat-sugerencias small text-muted mb-2";
    el.innerHTML = `<i class="fas fa-circle-question me-1"></i>¿Quisiste decir? ${sugerencias.map(enlaceArticulo).join('<span class="mx-1">·</span>')}`;
    bubble.after(el);
  }
  if (correccion) {
    const el = document.createElement("div");
    el.className = "kb-chat-correccion small text-muted mb-1";
    el.innerHTML = `<i class="fas fa-spell-check me-1"></i>Resultados para: <em>${escapeHtml(correccion)}</em>`;
    bubble.before(el);
  }
}

/**
 * Lee las burbujas .kb-chat-bubble dentro de un contenedor y devuelve
 * el historial como [{ role, content }] en orden cronologico.
 * Filtra burbujas vacias y mensajes "loading". Limita a los ultimos 10 turnos.
 */
export function collectChatHistory(msgs) {
  if (!msgs) return [];
  const bubbles = msgs.querySelectorAll(".kb-chat-bubble");
  const history = [];
  for (const b of bubbles) {
    const role = b.classList.contains("user") ? "user" : "assistant";
    const content = (b.innerText || b.textContent || "").trim();
    if (!content) continue;
    if (content.startsWith("Consultando la base de conocimiento")) continue;
    history.push({ role, content });
  }
  return history.slice(-10);
}

/**
 * Envia una pregunta al chat IA con streaming (o fallback sin streaming en mock).
 * Actualiza el loadingBubble incrementalmente con los tokens.
 * Resuelve con { fuentes, fuentesDetalle, correccion, sugerencias, text } (text: la respuesta completa, en markdown).
 */
export function askWithStream({ question, bubble, msgs, iconHtml, history, categoriaId = null }) {
  const icon = iconHtml ?? '<i class="fas fa-robot me-2 text-primary"></i>';

  // Mock mode: no streaming, respuesta completa
  if (AppConfig.api.useMock) {
    return ApiService.post("kb/ask", { question, history, categoriaId }).then((resp) => {
      bubble.innerHTML = `${icon}${marked.parse(resp.answer, { breaks: true })}`;
      const meta = metaRespuesta(resp);
      renderMetaRespuesta(bubble, meta);
      return { ...meta, text: resp.answer || "" };
    });
  }

  // Streaming mode
  return new Promise((resolve, reject) => {
    let text = "";
    bubble.innerHTML = `${icon}<span class="kb-stream-text"></span><span class="kb-stream-cursor">|</span>`;
    const textEl = bubble.querySelector(".kb-stream-text");
    const cursorEl = bubble.querySelector(".kb-stream-cursor");

    streamAsk(question, {
      history,
      categoriaId,
      onToken(token) {
        text += token;
        textEl.textContent = text;
        msgs.scrollTop = msgs.scrollHeight;
      },
      onDone(meta) {
        if (cursorEl) cursorEl.remove();
        bubble.innerHTML = `${icon}${marked.parse(text, { breaks: true })}`;
        renderMetaRespuesta(bubble, meta);
        resolve({ ...metaRespuesta(meta), text });
      },
      onError(err) {
        if (cursorEl) cursorEl.remove();
        reject(err);
      },
    });
  });
}
